/**
 * Find THE Claim for an encounter — the same way middleware does.
 *
 * There used to be three different answers to this question. The chart flattened
 * every Provenance target and took the first `Claim/` reference it saw (no
 * sorting), the Billing page's code editor walked a 20-row Provenance page, and
 * the server had its own rule. On an encounter that had accumulated more than one
 * Claim they could each pick a different one, which is exactly what made the two
 * screens show different codes for the same visit.
 *
 * Middleware now stamps every Claim with a per-encounter identifier and an
 * `item[].encounter` reference, so both lookups here are exact and indexed. The
 * Provenance walk survives only as a fallback for Claims written before that.
 */
import type { MedplumClient } from '@medplum/core'
import type { Claim } from '@medplum/fhirtypes'
import { BILLING_CLAIM_ENCOUNTER_IDENTIFIER_SYSTEM } from '@/model/middleware-types'

export async function resolveEncounterClaim(
  medplum: MedplumClient,
  encounterId: string,
): Promise<Claim | undefined> {
  const byIdentifier = await medplum.searchOne('Claim', {
    identifier: `${BILLING_CLAIM_ENCOUNTER_IDENTIFIER_SYSTEM}|${encounterId}`,
  })
  if (byIdentifier) return byIdentifier

  const byEncounter = await medplum.searchOne('Claim', {
    encounter: `Encounter/${encounterId}`,
    _sort: '-_lastUpdated',
  })
  if (byEncounter) return byEncounter

  // Legacy Claims carry neither stamp; their only link is the Provenance whose
  // `entity.what` is the Encounter. Newest first, so the most recent wins.
  const provenances = await medplum.searchResources('Provenance', {
    entity: `Encounter/${encounterId}`,
    _sort: '-recorded',
    _count: '50',
  })
  for (const provenance of provenances) {
    const claimId = (provenance.target ?? [])
      .map((target) => target.reference)
      .find((reference) => reference?.startsWith('Claim/'))
      ?.split('/')[1]
    if (claimId) {
      return medplum.readResource('Claim', claimId)
    }
  }

  return undefined
}
